
import Vue, { PropType } from 'vue';
import {
  CustomDropdown,
  CustomDropdownItem,
} from '@/libs/proof-approval/proofVersions/CustomDropdown';
import {
  AlignHorizontalLeft16,
  AlignHorizontalCenter16,
  AlignHorizontalRight16,
  AlignVerticalTop16,
  AlignVerticalCenter16,
  AlignVerticalBottom16,
  Crop16,
  Close16,
  WatsonHealthCircleMeasurement16,
  Stop16,
  StopFilledAlt16,
  WatsonHealthRotate_36016,
} from '@carbon/icons-vue';
import {
  CvIconButton,
  CvTextArea,
  CvButton,
  CvAccordion,
  CvAccordionItem,
  CvNumberInput,
} from '@carbon/vue';
import { PmsColor } from '@midocean/webapps-shared-domain';
import { mapState, mapActions } from 'pinia';
import { ColorDropdown } from '@/web-components/import-wizard/components/sections/color-management/ColorDropdown';
import TextCreation24 from '@carbon/icons-vue/es/text--creation/24';
import { useStore as useColorsManagementStore } from '@/stores/v2/colorsManagement';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useProductStore } from '@/stores/v2/product';
import ActionsPanel from './ActionsPanel.vue';
import IconTextButton from '../Buttons/IconTextButton.vue';
import { useStore as useShapeBoxSectionStore } from '@/stores/shapeBoxSection';
import { LogoShape } from '@/web-components/import-wizard/typings/pintura';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';
import { ProductPrintPositionAddModel } from '@/typings';

export default Vue.extend({
  name: 'ImagePanelOptions',
  components: {
    CustomDropdown,
    CustomDropdownItem,
    ColorDropdown,
    ActionsPanel,
    AlignHorizontalLeft16,
    AlignHorizontalCenter16,
    AlignHorizontalRight16,
    AlignVerticalTop16,
    AlignVerticalCenter16,
    AlignVerticalBottom16,
    Crop16,
    Close16,
    CvAccordion,
    CvAccordionItem,
    CvIconButton,
    CvTextArea,
    TextCreation24,
    CvButton,
    IconTextButton,
    CvNumberInput,
    WatsonHealthRotate_36016,
  },
  data() {
    return {
      attributes: {
        logoAlign: '',
        colorName: '',
        color: {} as PmsColor,
      },
      showRoundPrintPositionOptions: false,
      hover: false,
      Crop16,
      WatsonHealthCircleMeasurement16: WatsonHealthCircleMeasurement16,
      Stop16: Stop16,
      StopFilledAlt16: StopFilledAlt16,
      WatsonHealthRotate_36016: WatsonHealthRotate_36016,
    };
  },
  computed: {
    ...mapState(useColorsManagementStore, ['availablePmsColors']),
    ...mapState(useShapeStore, ['shapes', 'activeShapeId', 'panelPosition']),
    ...mapState(useShapeBoxSectionStore, ['isValidInput']),
    ...mapState(useProductStore, [
      'isLaserConverter',
      'selectedPrintPosition',
      'selectedVariant',
      'rotation',
      'activePosition',
      'isDebossingTechnique',
      'unavailablePrintPositions',
    ]),
    allPmsColors(): PmsColor[] {
      return this.availablePmsColors;
    },
    detectedPmsColors(): PmsColor[] {
      const array = this.activeShape?.pmsColorsInPngFile.map((color) => {
        return {
          ...color,
          name: color.name.includes('Closest Pantone: ')
            ? color.name.slice(17)
            : color.name,
        };
      });

      return array != null
        ? [...new Map(array.map((item) => [item['name'], item])).values()]
        : [];
    },
    activeShape(): LogoShape | undefined {
      var shape = this.shapes.find((shape) => shape.id === this.activeShapeId);
      console.log('shape', shape);
      if (shape != undefined && shape.kind === 'Logo') {
        return shape;
      }
      return undefined;
    },
    activeShapeRotation(): number {
      if (this.activeShape) {
        let rotation = ((this.activeShape.rotation as number) * 180) / Math.PI;

        if (rotation >= -90 && rotation < 0) {
          rotation = 360 + rotation;
        }
        return parseInt(rotation.toFixed());
      }
      return 1;
    },
    isRoundPosition(): boolean {
      return this.activePosition.printArea?.shapeType === 'Ellipse';
    },
    isRoundLogo(): boolean {
      if (this.activeShape) {
        return this.activeShape.isRoundLogo;
      }
      return false;
    },
  },
  methods: {
    ...mapActions(useShapeStore, [
      'removeShape',
      'deselectShape',
      'alignShape',
      'transformShape',
      'makeLogoRound',
      'changeLogoBackground',
    ]),
    ...mapActions(useProductStore, ['getVariantLaserColor', 'polygonDetails']),

    removeLogo() {
      this.removeShape(this.activeShapeId, true);
    },

    openLogoLibrary() {
      this.$emit('open-logo-library');
    },

    alignLogo(alignmentType: string) {
      this.attributes.logoAlign = alignmentType;
      this.alignShape(this.activeShapeId, alignmentType);
    },

    round(value: number, precision: number) {
      const multiplier = Math.pow(10, precision || 0);
      return Math.round(value * multiplier) / multiplier;
    },
    onUpdate(whatToUpdate: string, value: any) {
      this.transformShape(whatToUpdate, value);
    },
    rotateClockwise() {
      var newValue = this.activeShapeRotation + 90;
      if (newValue > 360) {
        newValue = newValue - 360;
      }
      this.transformShape('rotation', newValue);
    },
    rotateCounterClockwise() {
      var newValue = this.activeShapeRotation - 90;
      if (newValue < 0) {
        newValue = newValue + 360;
      }
      this.transformShape('rotation', newValue);
    },
    async replaceWith(index: number, color: string) {
      if (this.detectedPmsColors[index].name === color) {
        // if the same color is selected, do nothing.
        return;
      }

      const selectedPmsColor =
        this.availablePmsColors?.find((c: PmsColor) => {
          return c.name === color;
        }) || undefined;

      if (selectedPmsColor) {
        const shapeStore = useShapeStore();
        await shapeStore.replaceColor(
          this.detectedPmsColors[index],
          selectedPmsColor
        );

        const eventData = this.unavailablePrintPositions.filter(
          (u) => u.positionId === this.selectedPrintPosition.id
        ) as ProductPrintPositionAddModel[];

        const eventPayload = generateEventPayloadV2();

        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });

        this.$emit('action:change-color', eventData);

        // TODO - fix when snapshots are implemented
        //const historyStore = useHistoryStore();
        //const snapshot = createStateSnapshot();
        //historyStore.recordHistory(snapshot);
      }
    },

    async colorToRemove(color: PmsColor) {
      const shapeStore = useShapeStore();
      await shapeStore.removeColor(color);

      const eventData = this.unavailablePrintPositions.filter(
        (u) => u.positionId === this.selectedPrintPosition.id
      ) as ProductPrintPositionAddModel[];

      this.$emit('action:change-color', eventData);

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      // TODO - fix when snapshots are implemented
      //const snapshot = createStateSnapshot();
      //this.recordHistory(snapshot);
    },
    roundLogo() {
      this.makeLogoRound();
    },
    removeWhiteFull() {
      this.changeLogoBackground('fullBackground');
    },
    removeWhiteOutside() {
      this.changeLogoBackground('outsideBackground');
    },
  },
});
